import { toNameFloatMap } from "./nameFloatMap";

/**
 * Raw Unity `MaterialData` row as decoded from Dynamo.
 *
 * Storage path mirrors `DynamoClient.ToMaterialData`:
 * `pk = "{customerId}:material"`, `sk = "$material:{materialId}"`.
 */
export interface MaterialDto {
  readonly ID: string;
  readonly Name?: string;
  readonly ShaderName?: string;
  readonly UserPreset?: boolean;
  readonly WorkflowMode?: number | string;
  readonly Surface?: number | string;
  readonly Cull?: number | string;
  readonly Smoothness?: number | string;
  readonly Metallic?: number | string;
  readonly BaseColor?: unknown;
  readonly SpecColor?: unknown;
  readonly EmissionColor?: unknown;
  readonly FresnelColor?: unknown;
  readonly Tiling?: unknown;
  readonly Offset?: unknown;
  readonly Textures?: unknown;
  readonly FilterModes?: unknown;
  readonly FresnelPower?: number | string;
  readonly BumpScale?: number | string;
  readonly SmoothnessLow?: number | string;
  readonly OcclusionStrength?: number | string;
  readonly ClearCoat?: number | string;
  readonly ClearCoatGlossiness?: number | string;
  readonly ClearCoatGlossinessScale?: number | string;
  readonly Cutoff?: number | string;
  readonly UseVertexColor?: number | string;
  readonly [extra: string]: unknown;
}

export function materialNumber(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function materialColorHex(value: unknown, fallback: string): string {
  const map = toNameFloatMap(value);
  const hasRgb =
    typeof map.r === "number" ||
    typeof map.g === "number" ||
    typeof map.b === "number";
  if (!hasRgb) return fallback;
  return `#${toHex(map.r ?? 0)}${toHex(map.g ?? 0)}${toHex(map.b ?? 0)}`;
}

export function materialColorAlpha(value: unknown, fallback: number): number {
  const map = toNameFloatMap(value);
  return typeof map.a === "number" ? map.a : fallback;
}

export function materialFloatMap(value: unknown): Readonly<Record<string, number>> {
  return toNameFloatMap(value);
}

export function materialStringMap(value: unknown): Record<string, string> {
  if (!value) return {};
  if (Array.isArray(value)) {
    const out: Record<string, string> = {};
    for (const pair of value as RawNameStringPair[]) {
      const key = pair?.Name ?? pair?.name;
      const val = pair?.Value ?? pair?.value ?? pair?.Val ?? pair?.val;
      if (typeof key === "string" && typeof val === "string") out[key] = val;
    }
    return out;
  }
  if (typeof value !== "object") return {};

  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (typeof val === "string") out[key] = val;
  }
  return out;
}

function toHex(value: number): string {
  return Math.round(Math.max(0, Math.min(1, value)) * 255)
    .toString(16)
    .padStart(2, "0");
}

type RawNameStringPair = {
  readonly Name?: string;
  readonly name?: string;
  readonly Value?: string;
  readonly value?: string;
  readonly Val?: string;
  readonly val?: string;
};
